import { useCallback, useEffect, useState } from "react";
import {
  FileText,
  Upload,
  Download,
  Trash2,
  CheckCircle2,
  BookOpen,
  Scale,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState, formatDateTime } from "../ui";
import { useAuth } from "../AuthContext";

const DOCUMENTS = [
  {
    key: "estatuto",
    testid: "estatuto",
    title: "Estatuto Social",
    subtitle: "Documento constitutivo da ABLEMP.",
    icon: BookOpen,
    accent: "#002776",
    getUrl: "/portal/estatuto",
    downloadUrl: "/portal/estatuto/download",
    adminUrl: "/admin/estatuto",
    defaultTitle: "Estatuto Social",
    descriptionPlaceholder: "Ex.: Versão aprovada em assembleia de 03/2026",
    filenameFallback: "estatuto.pdf",
  },
  {
    key: "codigo-etica",
    testid: "codigo-etica",
    title: "Código de Ética e Conduta",
    subtitle:
      "Princípios, valores e regras de conduta que orientam associados, conselheiros e diretores.",
    icon: Scale,
    accent: "#009B3A",
    getUrl: "/portal/codigo-etica",
    downloadUrl: "/portal/codigo-etica/download",
    adminUrl: "/admin/codigo-etica",
    defaultTitle: "Código de Ética e Conduta",
    descriptionPlaceholder:
      "Ex.: Versão vigente aprovada pelo Conselho em 03/2026",
    filenameFallback: "codigo-etica.pdf",
  },
];

export default function Estatuto() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div data-testid="estatuto-page">
      <PageHeader
        title="Documentos Institucionais"
        subtitle={
          isAdmin
            ? "Publique e atualize os documentos institucionais da ABLEMP. Os associados poderão consultá-los e baixá-los."
            : "Consulte e baixe os documentos institucionais vigentes da ABLEMP."
        }
      />

      <div className="space-y-6">
        {DOCUMENTS.map((cfg) => (
          <DocumentSection key={cfg.key} cfg={cfg} isAdmin={isAdmin} />
        ))}
      </div>
    </div>
  );
}

function DocumentSection({ cfg, isAdmin }) {
  const Icon = cfg.icon;
  const [doc, setDoc] = useState(undefined); // undefined=loading, null=none, obj=present
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: cfg.defaultTitle,
    description: "",
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () =>
      api
        .get(cfg.getUrl)
        .then((r) => setDoc(r.data && r.data.key ? r.data : null))
        .catch(() => setDoc(null)),
    [cfg.getUrl]
  );

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Selecione o arquivo PDF.");
      return;
    }
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("description", form.description);
    fd.append("file", file);
    setBusy(true);
    try {
      await api.post(cfg.adminUrl, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${cfg.title} atualizado`);
      setOpen(false);
      setFile(null);
      setForm({ title: cfg.defaultTitle, description: "" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remover ${cfg.title.toLowerCase()} atual?`)) return;
    try {
      await api.delete(cfg.adminUrl);
      toast.success(`${cfg.title} removido`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const download = async () => {
    try {
      const res = await api.get(cfg.downloadUrl, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc?.file_name || cfg.filenameFallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch {
      toast.error("Documento não disponível.");
    }
  };

  return (
    <Card className="p-6" testid={`${cfg.testid}-section`}>
      <div className="flex items-start justify-between gap-4 mb-1">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div
            className="h-12 w-12 grid place-items-center rounded-sm shrink-0"
            style={{ background: `${cfg.accent}14`, color: cfg.accent }}
          >
            <Icon size={20} strokeWidth={1.5} />
          </div>
          <div className="min-w-0">
            <h2 className="font-serif-display text-2xl leading-tight">
              {cfg.title}
            </h2>
            <p className="mt-1 text-xs text-slate-500">{cfg.subtitle}</p>
          </div>
        </div>
        {isAdmin && (
          <button
            data-testid={`${cfg.testid}-upload-btn`}
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] shrink-0"
          >
            <Upload size={14} />
            {doc ? "Substituir" : "Enviar"}
          </button>
        )}
      </div>

      {open && isAdmin && (
        <div className="mt-5 border-t border-slate-100 pt-5">
          <form
            data-testid={`${cfg.testid}-form`}
            onSubmit={submit}
            className="grid md:grid-cols-2 gap-3"
          >
            <div>
              <Label>Título *</Label>
              <input
                data-testid={`${cfg.testid}-title`}
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Descrição</Label>
              <textarea
                data-testid={`${cfg.testid}-description`}
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder={cfg.descriptionPlaceholder}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[80px]"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Arquivo PDF *</Label>
              <input
                data-testid={`${cfg.testid}-file`}
                type="file"
                accept="application/pdf,application/*"
                required
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 w-full text-sm"
              />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button
                data-testid={`${cfg.testid}-submit`}
                disabled={busy}
                className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
              >
                {busy ? "Enviando..." : "Salvar documento"}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-slate-400"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="mt-5 border-t border-slate-100 pt-5">
        {doc === undefined ? (
          <div className="text-sm text-slate-500">Carregando...</div>
        ) : doc === null ? (
          <EmptyState
            title={`${cfg.title} ainda não publicado`}
            hint={
              isAdmin
                ? "Envie o PDF. Ele ficará disponível para todos os associados."
                : "Aguarde o administrador publicar este documento."
            }
          />
        ) : (
          <div className="flex items-start gap-5">
            <div
              className="h-14 w-14 grid place-items-center rounded-sm shrink-0"
              style={{ background: `${cfg.accent}14`, color: cfg.accent }}
            >
              <FileText size={22} strokeWidth={1.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-emerald-700">
                <CheckCircle2 size={12} /> Documento vigente
              </div>
              <h3 className="mt-2 font-serif-display text-xl">{doc.title}</h3>
              <div className="mt-1 text-xs text-slate-500">
                {doc.file_name} · atualizado em{" "}
                {formatDateTime(doc.uploaded_at)}
              </div>
              {doc.description && (
                <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">
                  {doc.description}
                </p>
              )}
              <div className="mt-5 flex gap-2 flex-wrap">
                <button
                  data-testid={`${cfg.testid}-download`}
                  onClick={download}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
                >
                  <Download size={14} /> Baixar PDF
                </button>
                {isAdmin && (
                  <button
                    data-testid={`${cfg.testid}-delete`}
                    onClick={remove}
                    className="inline-flex items-center gap-2 px-4 py-2.5 border border-rose-300 text-rose-700 text-[11px] uppercase tracking-[0.22em] hover:bg-rose-50"
                  >
                    <Trash2 size={14} /> Remover
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
    {children}
  </label>
);
